/**
 * Pool Page - Gestion Piscine
 * Disjoncteur PAC+Pompe : durée d'utilisation, consommation et coût par période.
 * Préparation du futur projet panneaux solaires + batterie (Shelly + Anker/Zendure).
 */
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import {
  Waves, Thermometer, Power, Lightbulb, Zap, AlertTriangle,
  Clock, Euro, Activity, Sun, Battery,
} from 'lucide-react';

const PRICE_PER_KWH = 0.1883;

const isPoolBreaker = (device) => {
  if (!device) return false;
  const name = (device.name || '').toLowerCase();
  const category = (device.category || '').toLowerCase();
  if (!['dlq', 'cz', 'tdq'].includes(category)) return false;
  return /piscine|pool|pac\b|pompe.*pisc|pisc.*pompe/.test(name);
};

const PERIOD_DEFS = [
  { key: 'day', label: 'JOUR' },
  { key: 'week', label: 'SEMAINE' },
  { key: 'month', label: 'MOIS' },
  { key: 'season', label: 'SAISON' },
];

const PoolBreakerCard = ({ device, stats, loadingStats }) => {
  const status = device?.status
    ? Object.fromEntries(device.status.map(s => [s.code, s.value]))
    : {};
  const watts = status.cur_power !== undefined ? status.cur_power / 10 : null;
  const volts = status.cur_voltage !== undefined ? status.cur_voltage / 10 : null;
  const amps = status.cur_current !== undefined ? status.cur_current / 1000 : null;
  const isOn = status.switch ?? status.switch_1 ?? false;

  return (
    <div className="bg-gradient-to-br from-cyan-950/60 to-blue-950/40 border-2 border-cyan-500/40 rounded-lg p-4" data-testid="pool-breaker-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-full bg-cyan-900/50 border border-cyan-500/40">
            <Activity className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-cyan-400 font-bold font-mono text-sm">{device.name}</h3>
            <div className="text-[10px] font-mono text-gray-400 flex items-center gap-2">
              <span className={device.online ? 'text-green-400' : 'text-red-400'}>
                ● {device.online ? 'ONLINE' : 'HORS LIGNE'}
              </span>
              <span>{isOn ? 'EN MARCHE' : 'ARRÊT'}</span>
            </div>
          </div>
        </div>
        <div className={`text-2xl font-bold font-mono ${isOn ? 'text-green-400' : 'text-gray-500'}`}>
          {isOn ? 'ON' : 'OFF'}
        </div>
      </div>

      {/* Live measurements */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-black/40 border border-cyan-500/20 rounded p-2 text-center" data-testid="pool-watts">
          <div className="text-[8px] font-mono text-gray-500 uppercase">Puissance</div>
          <div className="text-2xl font-mono font-bold text-yellow-400">
            {watts !== null ? Math.round(watts) : '--'}
            <span className="text-xs ml-0.5">W</span>
          </div>
        </div>
        <div className="bg-black/40 border border-cyan-500/20 rounded p-2 text-center" data-testid="pool-volts">
          <div className="text-[8px] font-mono text-gray-500 uppercase">Tension</div>
          <div className="text-2xl font-mono font-bold text-blue-400">
            {volts !== null ? Math.round(volts) : '--'}
            <span className="text-xs ml-0.5">V</span>
          </div>
        </div>
        <div className="bg-black/40 border border-cyan-500/20 rounded p-2 text-center" data-testid="pool-amps">
          <div className="text-[8px] font-mono text-gray-500 uppercase">Intensité</div>
          <div className="text-2xl font-mono font-bold text-orange-400">
            {amps !== null ? amps.toFixed(2) : '--'}
            <span className="text-xs ml-0.5">A</span>
          </div>
        </div>
      </div>

      {/* Stats par période — Durée / Conso / Coût */}
      <div className="bg-black/40 border border-cyan-500/30 rounded-lg overflow-hidden" data-testid="pool-stats-table">
        <div className="grid grid-cols-4 gap-px bg-cyan-500/20 text-[8px] font-mono uppercase">
          <div className="bg-black p-2 text-cyan-300 font-bold">Période</div>
          <div className="bg-black p-2 text-cyan-300 font-bold flex items-center gap-1"><Clock className="w-3 h-3" />Durée</div>
          <div className="bg-black p-2 text-cyan-300 font-bold flex items-center gap-1"><Zap className="w-3 h-3" />Conso</div>
          <div className="bg-black p-2 text-cyan-300 font-bold flex items-center gap-1"><Euro className="w-3 h-3" />Coût</div>
        </div>
        {PERIOD_DEFS.map(({ key, label }) => {
          const s = stats?.[key];
          const isEmpty = !s || !s.has_history;
          return (
            <div key={key} className="grid grid-cols-4 gap-px bg-cyan-500/10 text-[11px] font-mono" data-testid={`pool-stats-${key}`}>
              <div className="bg-black/60 p-2 text-gray-400 font-bold">{label}</div>
              <div className="bg-black/60 p-2 text-cyan-300">
                {loadingStats ? '...' : (s ? s.duration_label : '--')}
              </div>
              <div className="bg-black/60 p-2 text-yellow-300">
                {loadingStats ? '...' : (s ? `${s.energy_kwh.toFixed(2)} kWh` : '--')}
              </div>
              <div className="bg-black/60 p-2 text-green-400 font-bold">
                {loadingStats ? '...' : (s ? `${s.cost_eur.toFixed(2)} €` : '--')}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-2 text-[9px] font-mono text-gray-500 text-center">
        💡 Données réelles agrégées toutes les 60 s · Tarif Total Énergies 0.1883 €/kWh
      </div>
    </div>
  );
};

const Placeholder = () => (
  <div className="bg-yellow-900/20 border-2 border-dashed border-yellow-500/40 rounded-lg p-6 text-center" data-testid="pool-placeholder">
    <AlertTriangle className="w-10 h-10 text-yellow-400 mx-auto mb-3" />
    <div className="text-yellow-400 font-mono font-bold mb-2">DISJONCTEUR PAC+POMPE PISCINE</div>
    <div className="text-yellow-300/70 text-sm mb-3">En attente d'installation</div>
    <div className="text-xs text-gray-400 font-mono space-y-1 max-w-md mx-auto">
      <p>Pour activer cette section :</p>
      <ol className="text-left list-decimal list-inside mt-2 space-y-1">
        <li>Installer un disjoncteur connecté Tuya (catégorie <code className="bg-black/40 px-1">dlq</code>) sur la ligne commune PAC + Pompe</li>
        <li>L'ajouter dans SmartLife sous le nom <code className="bg-black/40 px-1">PAC+Pompe Piscine</code></li>
        <li>Aller dans <code className="bg-black/40 px-1">Admin → AUTO-DÉCOUVERTE</code></li>
      </ol>
      <p className="text-cyan-400 mt-3">Le tableau Durée / Conso / Coût se peuplera dès le premier polling (60 s).</p>
    </div>
  </div>
);

const FuturePvSection = () => (
  <div className="bg-gradient-to-br from-amber-900/20 to-purple-900/20 border border-amber-500/30 rounded-lg p-4" data-testid="future-pv-section">
    <h3 className="text-amber-400 font-mono font-bold text-sm mb-3 flex items-center gap-2">
      <Sun className="w-4 h-4" />
      PROJET AUTOCONSOMMATION SOLAIRE
    </h3>
    <div className="grid grid-cols-3 gap-3 text-[10px] font-mono">
      <div className="bg-black/40 border border-amber-500/20 rounded p-3">
        <div className="flex items-center gap-1 text-amber-300 font-bold mb-1">
          <Sun className="w-3 h-3" /> PANNEAUX PV
        </div>
        <div className="text-gray-400">Production solaire dédiée à la piscine (zone PAC+Pompe).</div>
        <div className="text-gray-500 mt-1">À planifier</div>
      </div>
      <div className="bg-black/40 border border-amber-500/20 rounded p-3">
        <div className="flex items-center gap-1 text-amber-300 font-bold mb-1">
          <Activity className="w-3 h-3" /> SHELLY
        </div>
        <div className="text-gray-400">Mesure production PV instantanée + bascule réseau/PV.</div>
        <div className="text-gray-500 mt-1">Intégration future</div>
      </div>
      <div className="bg-black/40 border border-amber-500/20 rounded p-3">
        <div className="flex items-center gap-1 text-amber-300 font-bold mb-1">
          <Battery className="w-3 h-3" /> BATTERIE
        </div>
        <div className="text-gray-400">Anker Solix ou Zendure (onduleur intégré).</div>
        <div className="text-gray-500 mt-1">À choisir</div>
      </div>
    </div>
    <div className="mt-3 text-[10px] font-mono text-gray-400 leading-relaxed">
      Une fois l'installation déployée, HAL 3000 affichera ici en plus : production PV instantanée,
      taux d'autoconsommation, économies réalisées et état de charge batterie.
    </div>
  </div>
);

const Pool = () => {
  const [poolDevice, setPoolDevice] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);

  // Fetch device + stats every 60s
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const { data } = await api.get('/integrations/tuya/devices');
        if (cancelled) return;
        if (data?.success && data.devices) {
          const found = data.devices.find(isPoolBreaker);
          setPoolDevice(found || null);
          if (found) {
            setLoadingStats(true);
            try {
              const { data: stData } = await api.get(`/integrations/tuya/devices/${found.id}/usage-stats`);
              if (!cancelled && stData?.success) setStats(stData.stats);
            } catch (e) { /* silent */ }
            finally { if (!cancelled) setLoadingStats(false); }
          }
        }
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  return (
    <Layout title="Piscine">
      <div className="h-full overflow-y-auto pr-2 pb-3">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-cyan-900/50 border-2 border-cyan-500">
            <Waves className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-cyan-400 font-mono">GESTION PISCINE</h2>
            <p className="text-gray-400 text-xs">PAC + Pompe + Éclairages</p>
          </div>
        </div>

        {/* Section conso PAC + Pompe */}
        <section className="mb-4">
          <h3 className="text-cyan-400 font-mono font-bold text-sm mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4" />
            DISJONCTEUR PAC + POMPE
          </h3>
          {loading ? (
            <div className="bg-black/40 border border-gray-700 rounded-lg p-6 text-center text-gray-500 font-mono text-sm">
              Chargement...
            </div>
          ) : poolDevice ? (
            <PoolBreakerCard device={poolDevice} stats={stats} loadingStats={loadingStats} />
          ) : (
            <Placeholder />
          )}
        </section>

        {/* Section autres équipements */}
        <section className="mb-4">
          <h3 className="text-gray-400 font-mono font-bold text-sm mb-2 flex items-center gap-2">
            <Power className="w-4 h-4" />
            AUTRES ÉQUIPEMENTS
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: <Thermometer className="w-5 h-5" />, title: 'Pompe à Chaleur', status: 'Mesurée via disjoncteur PAC+Pompe' },
              { icon: <Waves className="w-5 h-5" />, title: 'Pompe de Circulation', status: 'Mesurée via disjoncteur PAC+Pompe' },
              { icon: <Lightbulb className="w-5 h-5" />, title: 'Éclairage Piscine', status: 'Équipement à connecter' },
              { icon: <Lightbulb className="w-5 h-5" />, title: 'Éclairage Extérieur', status: 'Équipement à connecter' },
            ].map((f, i) => (
              <div key={i} className="bg-black/40 border border-gray-800 rounded-lg p-3 opacity-60" data-testid={`pool-feature-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-gray-500">{f.icon}</div>
                  <span className="text-white font-bold text-xs">{f.title}</span>
                </div>
                <div className="text-[10px] text-gray-500">{f.status}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Section projet PV */}
        <FuturePvSection />
      </div>
    </Layout>
  );
};

export default Pool;
